import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  IconButton,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "../api/client";
import { getMyPrediction, saveMyPrediction } from "../api/tablePredictor";
import { getCurrentMatchweek, getLatestStandings } from "../api/premierLeague";
import { usePlSeason } from "../hooks/usePlSeason";
import { useAuth } from "../auth/AuthContext";
import PageHeader from "../components/PageHeader";

const TOP_BAR_BLUE = "#00005f";

export default function TablePredictorPage() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { seasonId: _hookSeasonId } = usePlSeason();
  const SEASON_ID = location.state?.seasonId ?? _hookSeasonId;
  const { user, logout } = useAuth();

  function handleLogout() {
    logout();
    navigate("/login");
  }

  const [teams, setTeams] = useState([]);
  const [standingsRows, setStandingsRows] = useState([]);
  const [positions, setPositions] = useState(Array(20).fill(""));
  const [locked, setLocked] = useState(false);
  const [lockAt, setLockAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noSeasonData, setNoSeasonData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [teamsRes, predRes, mwMeta] = await Promise.all([
          // Scoped to the season, or the list is every club ever in the
          // database — relegated sides included — rather than this year's 20.
          api.get("/games/premier_league/teams/", {
            params: { season_id: SEASON_ID },
          }),
          getMyPrediction(Number(poolId), SEASON_ID).catch(() => null),
          getCurrentMatchweek(SEASON_ID).catch(() => null),
        ]);

        const sortedTeams = [...teamsRes.data].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setTeams(sortedTeams);

        if (!mwMeta) {
          setNoSeasonData(true);
          setLoading(false);
          return;
        }

        if (predRes) {
          setLocked(predRes.locked);
          setLockAt(predRes.lock_at_utc);
          if (predRes.exists) {
            setPositions(predRes.positions.map((p) => (p ? String(p.team_id) : "")));
          }
        }

        // The meta has no `matchweek` key, so this read undefined and fell
        // back to 1 — the current table was the opening-day table all season.
        const standingsData = await getLatestStandings(SEASON_ID).catch(() => []);
        setStandingsRows(standingsData || []);
      } catch {
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    }
    if (SEASON_ID) load();
  }, [poolId, SEASON_ID]);

  const filledCount = positions.filter(Boolean).length;

  /**
   * Place a team, swapping with wherever it already sits.
   *
   * Every option used to be disabled once its team was placed, which meant
   * that with all twenty filled each row could only re-select what it already
   * held — the table was finished and frozen, and the only way to change your
   * mind was to start again. Moving a team now trades places with the row it
   * came from, so any order can be reached from any other.
   */
  function handleChange(index, value) {
    const next = [...positions];
    const existing = next.findIndex((v) => v && v === value);
    if (existing >= 0 && existing !== index) next[existing] = next[index] || "";
    next[index] = value;
    setPositions(next);
    setSuccess("");
  }

  function handleClear(index) {
    const next = [...positions];
    next[index] = "";
    setPositions(next);
    setSuccess("");
  }

  function handleClearAll() {
    setPositions(Array(20).fill(""));
    setSuccess("");
    setError("");
  }

  async function handleSave() {
    setError("");
    setSuccess("");

    const filled = positions.filter(Boolean);
    if (filled.length !== 20) {
      setError("You must select a team for all 20 positions.");
      return;
    }
    if (new Set(filled).size !== 20) {
      setError("Duplicate teams detected. Each team must appear exactly once.");
      return;
    }

    setSaving(true);
    try {
      await saveMyPrediction(Number(poolId), SEASON_ID, positions.map(Number));
      setSuccess("Prediction saved!");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || "Failed to save prediction.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="#00007f">
        <Spinner size="xl" color="white" />
      </Flex>
    );
  }

  const lockDate = lockAt
    ? new Date(lockAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  return (
    <Box minH="100vh" bg="#00007f" color="white">
      <PageHeader
        title="PL TABLE PREDICTOR"
        onBack={() => navigate(`/premier-league/current/${poolId}`, { state: { seasonId: SEASON_ID } })}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="1200px" py={8}>
        {/* Status banner */}
        {noSeasonData ? (
          <Alert status="info" borderRadius="md" mb={6} color={TOP_BAR_BLUE}>
            <AlertIcon />
            Data not yet available — the table predictor will open once the season begins.
          </Alert>
        ) : locked ? (
          <Alert status="warning" borderRadius="md" mb={6} color="black">
            <AlertIcon />
            Predictions are locked — MW4 has kicked off
            {lockDate ? ` (${lockDate})` : ""}.
          </Alert>
        ) : (
          <Alert status="info" borderRadius="md" mb={6} color="black">
            <AlertIcon />
            Predict the final Premier League table. Locked at first MW4 kickoff
            {lockDate ? ` — ${lockDate}` : ""}.
          </Alert>
        )}

        <Flex justify="flex-start" mb={4}>
          <Button
            size="sm"
            bg="white"
            color={TOP_BAR_BLUE}
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate(`/premier-league/current/${poolId}`, { state: { seasonId: SEASON_ID } })}
          >
            Return to Leaderboard
          </Button>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md" mb={4} color="black">
            <AlertIcon />
            {error}
          </Alert>
        )}
        {success && (
          <Alert status="success" borderRadius="md" mb={4} color="black">
            <AlertIcon />
            {success}
          </Alert>
        )}

        <HStack align="start" spacing={6} flexWrap="wrap">
          {/* Left — current live table */}
          <Box
            flex="1"
            minW="280px"
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
          >
            <Heading size="md" color={TOP_BAR_BLUE} mb={4}>
              Current Table
            </Heading>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Rank</Th>
                    <Th>Team</Th>
                    <Th isNumeric>Pts</Th>
                    <Th isNumeric>GD</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {standingsRows.length > 0 ? (
                    standingsRows.map((row) => {
                      const gd = row.goal_differential ?? 0;
                      return (
                        <Tr key={row.team_id ?? row.rank}>
                          <Td>{row.rank ?? "-"}</Td>
                          <Td>{row.team_name || "-"}</Td>
                          <Td isNumeric>{row.points ?? 0}</Td>
                          {/* Signed, the way a league table prints it — a bare
                              "7" reads as a total rather than a difference. */}
                          <Td isNumeric>{gd > 0 ? `+${gd}` : gd}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={4}>
                        <Text color="gray.500">No standings available.</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Right — prediction dropdowns */}
          <Box
            flex="1"
            minW="320px"
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
          >
            <Heading size="md" color={TOP_BAR_BLUE} mb={4}>
              Your Prediction
            </Heading>
            <VStack align="stretch" spacing={2}>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="50px">Pos</Th>
                    <Th>Team</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {positions.map((val, i) => {
                    const pos = i + 1;
                    return (
                      <Tr key={pos}>
                        <Td fontWeight="bold" color={TOP_BAR_BLUE} py={1}>
                          {pos}
                        </Td>
                        <Td py={1}>
                          <HStack spacing={1}>
                            <Select
                              size="sm"
                              h="30px"
                              fontSize="sm"
                              value={val}
                              onChange={(e) => handleChange(i, e.target.value)}
                              isDisabled={locked}
                              placeholder="— select team —"
                              borderColor={val ? TOP_BAR_BLUE : "gray.300"}
                            >
                              {teams.map((t) => {
                                const at = positions.indexOf(String(t.id));
                                const elsewhere = at >= 0 && at !== i;
                                return (
                                  <option key={t.id} value={String(t.id)}>
                                    {/* Saying where a team already sits makes
                                        the swap predictable rather than a
                                        surprise. */}
                                    {elsewhere ? `${t.name} (now ${at + 1})` : t.name}
                                  </option>
                                );
                              })}
                            </Select>
                            <IconButton
                              aria-label={`Clear position ${pos}`}
                              title="Remove this team"
                              icon={<CloseIcon boxSize="8px" />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleClear(i)}
                              isDisabled={locked || !val}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>

              {!locked && (
                <HStack mt={4} spacing={3}>
                  <Button
                    bg={TOP_BAR_BLUE}
                    color="white"
                    _hover={{ bg: "#00008f" }}
                    isLoading={saving}
                    onClick={handleSave}
                    isDisabled={filledCount !== 20}
                  >
                    Save Prediction
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="red"
                    onClick={handleClearAll}
                    isDisabled={saving || filledCount === 0}
                  >
                    Clear All
                  </Button>
                  <Text fontSize="xs" color="gray.500">
                    {filledCount}/20 placed
                  </Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </HStack>
      </Container>
    </Box>
  );
}
